import React from 'react';
import 'twin.macro';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import request from '@teetsh/app/src/utils/request';
import { useBillingInfosData } from '@teetsh/app/src/queries/useBillingInfos';
import { useTranslation } from 'react-i18next';
import ButtonLink from '@teetsh/app/src/components/Buttons/Link';
import { SEQUENCES_TRASH_KEY } from '@teetsh/app/src/containers/UserSettings/Trash/Sequences/useSequencesTrash';
import { SEQUENCES_LIST_KEY } from '@teetsh/app/src/queries/sequences/useSequencesFolders';
import { LimitReachedErrorResponse } from '@teetsh/app/src/types/errors';

export function useRestoreSequence() {
  const { notifySuccess, notifyError } = useNotification();
  const queryClient = useQueryClient();
  const billingInfos = useBillingInfosData();
  const { t } = useTranslation(['common', 'userSettings']);
  const { mutateAsync } = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      request(`/sequences/${id}/restore`, { method: 'PATCH' }),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [SEQUENCES_TRASH_KEY],
      });
      queryClient.invalidateQueries({
        queryKey: [SEQUENCES_LIST_KEY],
      });
      notifySuccess({
        title: t('userSettings:restore.messages.success'),
        message: (
          <ButtonLink outlined intent="success" to={`/sequences/${id}`}>
            {t('userSettings:restore.messages.see')}
          </ButtonLink>
        ),
      });
    },
    onError: ({ response }) => {
      if (response?.status === 403 && (response?.data as any)?.requiredPlan) {
        // TODO improve that part (handle !billingInfos)
        const { requiredPlan, limit } =
          response?.data as LimitReachedErrorResponse;
        notifyError({
          title: `${t(
            `common:components.limitReached.description${
              billingInfos?.isOnOldPricing ? 'OnOldPlan' : ''
            }`,
            {
              limit,
              itemLabel: t(
                'common:components.limitReached.labels.programmation',
                {
                  count: limit,
                },
              ),
              currentPlanLabel: billingInfos?.currentPlanLabel,
            },
          )}.`,
          message: `${t(
            `common:components.limitReached.upgradeHint${
              billingInfos?.isOnOldPricing ? 'OnOldPlan' : ''
            }`,
            {
              requiredPlan,
            },
          )}.`,
        });
        return;
      }

      notifyError({ title: t('userSettings:restore.messages.error') });
    },
  });
  return mutateAsync;
}
